import { IconName, MenuContentProps } from '@orbita-ui/core';
import { MenuItemLink, MenuItemNav, MenuItemSection, MenuItems } from './MenuItemsModel';
import { NavigateFunction } from 'react-router-dom';
import { AppMenuContentProps, SubMenuContentProps } from './MenuContentProps';

function checkPermissions(
  userPermissions: string[],
  permissionsToCheck: Set<string>,
): boolean {
  if (permissionsToCheck.has('PERMITIR')) {
    return true;
  }
  for (const permission of userPermissions) {
    if (permissionsToCheck.has(permission)) {
      return true;
    }
  }
  return false;
}

class MenuSectionBuilder {
  abbr: string;
  name: string;
  items: MenuItemNavBuilder[];

  constructor(data: MenuItemSection, userPermissions: string[]) {
    if (!data?.items) {
      console.error(`MenuSection: sección del menu ${data.name} mal formado`);
      throw new Error(`MenuSection: sección del menu ${data.name} mal formado`);
    }
    this.abbr = data.abbr;
    this.name = data.name;
    const functions = new Set<string>();
    this.items = data.items.reduce((arr, item) => {
      try {
        const menuItem = new MenuItemNavBuilder(item, userPermissions);
        arr.push(menuItem);
        menuItem.functions.forEach((fn) => functions.add(fn));
      } catch {}
      return arr;
    }, []);
    if (!checkPermissions(userPermissions, functions)) {
      throw new Error();
    }
  }

  build({
    selectedPath,
    navigateFunction,
  }: {
    selectedPath: string;
    navigateFunction: NavigateFunction;
  }): MenuContentProps[] {
    const menuIems: MenuContentProps[] = this.name
      ? [
          {
            abbr: this.abbr,
            itemType: 'title',
            text: this.name,
          },
        ]
      : [];
    return [
      ...menuIems,
      ...this.items.map((item) => item.build({ selectedPath, navigateFunction })),
      {
        itemType: 'divider',
      },
    ];
  }
}

class MenuItemNavBuilder {
  icon: IconName;
  items: MenuItemLinkBuilder[];
  name: string;
  functions: Set<string>;
  path?: string;

  constructor(data: MenuItemNav, userPermissions: string[]) {
    if (!data?.name || (!data?.items && !data?.path) || (data?.path && !data.functions)) {
      console.error(`MenuItemType: item del menu ${data.name} mal formado`);
      throw new Error(`MenuItemType: item del menu ${data.name} mal formado`);
    }
    this.icon = data.icon;
    this.name = data.name;
    this.path = data.path;
    const functions = new Set<string>(data.functions);
    if (data.items) {
      this.items = data.items.reduce((arr, item) => {
        try {
          const menuItem = new MenuItemLinkBuilder(item, userPermissions);
          arr.push(menuItem);
          menuItem.functions.forEach((fn) => functions.add(fn));
        } catch {}
        return arr;
      }, []);
    }
    this.functions = functions;
    if (!checkPermissions(userPermissions, this.functions)) {
      throw new Error();
    }
  }

  build({
    selectedPath,
    navigateFunction,
  }: {
    selectedPath: string;
    navigateFunction: NavigateFunction;
  }): AppMenuContentProps {
    return {
      iconName: this.icon,
      itemType: 'menuItem',
      text: this.name,
      selected: this.isSelected(selectedPath),
      subItems: this.items?.map((item) => item.build({ selectedPath, navigateFunction })),
      action: () => navigateFunction(this.path),
      path: this.path,
    };
  }

  isSelected(selectedPath: string): boolean {
    return (
      selectedPath === this.path ||
      this.items?.some((item) => item.isSelected(selectedPath))
    );
  }
}

class MenuItemLinkBuilder {
  name: string;
  path: string;
  functions: Set<string>;

  constructor(data: MenuItemLink, userPermissions: string[]) {
    if (!data?.name || !data?.path || !data?.functions) {
      console.error(`ItemType: item del menu ${data.name} mal formado`);
      throw new Error(`ItemType: item del menu ${data.name} mal formado`);
    }
    this.name = data.name;
    this.path = data.path;
    this.functions = new Set(data.functions);
    if (!checkPermissions(userPermissions, this.functions)) {
      throw new Error();
    }
  }

  build({
    selectedPath,
    navigateFunction,
  }: {
    selectedPath: string;
    navigateFunction: NavigateFunction;
  }): SubMenuContentProps {
    return {
      text: this.name,
      selected: this.isSelected(selectedPath),
      action: () => navigateFunction(this.path),
      path: this.path,
    };
  }

  isSelected(selectedPath: string): boolean {
    return selectedPath === this.path;
  }
}

export class MenuBuilder {
  menuItems: MenuItems;
  selectedPath: string;
  permissions: string[];
  navigateFn: NavigateFunction;

  addItems(menuItems: MenuItems): this {
    this.menuItems = menuItems;
    return this;
  }

  /**
   * Este metodo se deja por compatibilidad con el menu de orbita
   */
  setSelectedPath(selectedPath: string): this {
    this.selectedPath = selectedPath;
    return this;
  }

  /**
   * Este metodo se deja por compatibilidad con el menu de orbita
   */
  setNavigateFn(fn: NavigateFunction): this {
    this.navigateFn = fn;
    return this;
  }

  filterUserPermisions(permissions: string[]): this {
    this.permissions = permissions;
    return this;
  }

  build(): AppMenuContentProps[] {
    if (!this.permissions) {
      return [];
    }
    const items = this.menuItems.sections.reduce((arr, section) => {
      try {
        return arr.concat(
          new MenuSectionBuilder(section, this.permissions).build({
            selectedPath: this.selectedPath,
            navigateFunction: this.navigateFn,
          }),
        );
      } catch {}
      return arr;
    }, []);
    return items.slice(0, -1);
  }
}
